import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Typography, Paper, useTheme,
  Stack, Skeleton, alpha, Chip, Divider,
} from '@mui/material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  AreaChart, Area, PieChart, Pie,
} from 'recharts';
import {
  Block as BlockIcon, TrendingUp, TrendingDown, TrendingFlat,
  Payments as PaymentsIcon, AccountBalance, ReceiptLong,
  CreditCard, WarningAmberOutlined,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import ChartCard from '../../components/reports/finance/ChartCard';
import HorizontalBarList from '../../components/reports/finance/HorizontalBarList';
import { formatMoney, formatMoneyFull } from '../../components/reports/finance/chartConfig';

import feeReportsService from '../../services/feeReportsService';
import SearchableSelect from '../../components/common/SearchableSelect';
import { buildSessionOptions } from '../../utils/sessionOptions';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { useAuth } from '../../context/AuthContext';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useSubscription } from '../../context/SubscriptionContext';

import {
  AgingResponse, CollectionRateResponse, ByCategoryResponse, ForecastResponse,
  CollectionTrendResponse, TopOutstandingStudentsResponse, PaymentMethodMixResponse,
  AdjustmentsSummaryResponse, FeePaymentMethodKey,
} from '../../types/fees';
import { Session, SessionClass } from '../../types/sessions';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { PAYMENT_METHOD_LABEL } from '../../utils/paymentMethods';

/**
 * Fee Reports — four-beat story.
 *
 *   1. KPI strip           — Billed / Collected / Outstanding / Collection %
 *   2. Aging               — 5-bucket vertical bar chart of overdue-days
 *   3. Composition (two)   — By Category (billed vs collected) + By Class (rate)
 *   4. Forecast            — Upcoming dues per-day for the next 30 / 90 days
 *
 * Filters (session / class / horizon) apply globally so every card reads
 * from the same window. Every card is a `ChartCard` shell for consistent
 * loading + empty states.
 */

type Horizon = 7 | 14 | 30 | 60 | 90;

const HORIZON_OPTIONS: { value: Horizon; label: string }[] = [
  { value: 7,  label: 'Next 7 days' },
  { value: 14, label: 'Next 14 days' },
  { value: 30, label: 'Next 30 days' },
  { value: 60, label: 'Next 60 days' },
  { value: 90, label: 'Next 90 days' },
];

// Method donut palette. Chosen so light/dark modes both look intentional
// and adjacent slices contrast well. Not derived from the categorical palette
// because payment methods have a stable identity (Cash always = green).
const methodPalette = (theme: any): Record<FeePaymentMethodKey, string> => ({
  cash:          theme.palette.success.main,
  bank_transfer: theme.palette.primary.main,
  cheque:        theme.palette.warning.main,
  online:        theme.palette.info.main,
});

const methodIcon: Record<FeePaymentMethodKey, React.ReactNode> = {
  cash:          <PaymentsIcon fontSize="small" />,
  bank_transfer: <AccountBalance fontSize="small" />,
  cheque:        <ReceiptLong fontSize="small" />,
  online:        <CreditCard fontSize="small" />,
};

const FeeReportsPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { currency } = useAuth();
  const { canRead } = useModulePermissions();
  const { hasFeature } = useSubscription();
  const navigate = useNavigate();

  const [sessionId, setSessionId] = useState<string>('');
  const [classId, setClassId] = useState<string>('');
  const [horizon, setHorizon] = useState<Horizon>(30);
  // Per-widget class filter on the Top Outstanding Students card. Defaults to
  // the page-level filter so users see the same scope on first load, but can
  // drill into a specific class without disturbing the other reports.
  const [topStudentsClassId, setTopStudentsClassId] = useState<string>('');
  const [topStudentsLoading, setTopStudentsLoading] = useState(false);

  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);

  const [aging, setAging] = useState<AgingResponse | null>(null);
  const [collection, setCollection] = useState<CollectionRateResponse | null>(null);
  const [byCategory, setByCategory] = useState<ByCategoryResponse | null>(null);
  const [forecast, setForecast] = useState<ForecastResponse | null>(null);
  const [trend, setTrend] = useState<CollectionTrendResponse | null>(null);
  const [topStudents, setTopStudents] = useState<TopOutstandingStudentsResponse | null>(null);
  const [methodMix, setMethodMix] = useState<PaymentMethodMixResponse | null>(null);
  const [adjustments, setAdjustments] = useState<AdjustmentsSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const currencyFmt = useCurrencyFormat();

  // ─── Load pickers ─────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ss, cls] = await Promise.all([
          sessionsApi.list({ includeArchived: false }),
          classesApi.list(),
        ]);
        if (cancelled) return;
        setSessions(ss);
        setClasses(cls);
        if (!sessionId) {
          const active = ss.find(s => s.status === 'active') ?? ss[0];
          if (active) setSessionId(active._id);
        }
      } catch {
        if (!cancelled) enqueueSnackbar('Failed to load pickers', { variant: 'error' });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Load reports in parallel ────────────────────────────────────────────
  //
  // Note: we intentionally do NOT pass dateFrom/dateTo. Scoping by sessionId
  // alone captures every invoice that belongs to the session, regardless of
  // dueDate. Filtering by [session.startDate, session.endDate] used to hide
  // invoices whose dueDate happened to fall outside the session window
  // (schedule drift, legacy invoices, adjusted plans) — which caused Billed /
  // Collected / Outstanding to render as PKR 0 even when aging showed dues.

  const load = useCallback(async () => {
    if (!sessionId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [a, c, cat, f, t, mm, adj] = await Promise.all([
        feeReportsService.getAging({ sessionId, classId: classId || undefined, breakBy: 'class' }),
        feeReportsService.getCollectionRate({ sessionId, classId: classId || undefined, groupBy: 'class' }),
        feeReportsService.getByCategory({ sessionId, classId: classId || undefined }),
        feeReportsService.getDuesForecast({ sessionId, classId: classId || undefined, horizonDays: horizon }),
        feeReportsService.getCollectionTrend({ sessionId, classId: classId || undefined, months: 12 }),
        feeReportsService.getPaymentMethodMix({ sessionId, classId: classId || undefined }),
        feeReportsService.getAdjustmentsSummary({ sessionId, classId: classId || undefined }),
      ]);
      setAging(a); setCollection(c); setByCategory(cat); setForecast(f);
      setTrend(t); setMethodMix(mm); setAdjustments(adj);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load fee reports', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [sessionId, classId, horizon, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // Page-level class filter is the widget's default. When the page filter
  // moves, the widget follows so scope stays coherent unless the user
  // explicitly overrides on the card.
  useEffect(() => { setTopStudentsClassId(classId); }, [classId]);

  // Top Outstanding Students fetches on its own so its class picker can
  // narrow the list without triggering a full page reload.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setTopStudentsLoading(true);
    (async () => {
      try {
        const ts = await feeReportsService.getTopOutstandingStudents({
          sessionId,
          classId: topStudentsClassId || undefined,
          limit: 20,
        });
        if (!cancelled) setTopStudents(ts);
      } catch (err: any) {
        if (!cancelled) enqueueSnackbar(err?.message || 'Failed to load top outstanding students', { variant: 'error' });
      } finally {
        if (!cancelled) setTopStudentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, topStudentsClassId, enqueueSnackbar]);

  // ─── Derived / display data ──────────────────────────────────────────────

  const kpi = collection?.total;

  const agingChartData = useMemo(
    () => (aging?.buckets ?? []).map(b => ({
      label: b.label,
      outstanding: b.outstanding,
      count: b.count,
      isOverdue: b.key !== 'current',
    })),
    [aging],
  );

  const agingByClassRows = useMemo(
    () => (aging?.byGroup ?? []).slice(0, 10).map(g => ({
      label: g.name,
      amount: g.outstanding,
      count: g.count,
    })),
    [aging],
  );

  const collectionByClassRows = useMemo(
    () => (collection?.byGroup ?? []).slice(0, 10).map(g => ({
      label: g.name,
      amount: g.billed,
      count: g.count,
      // Additional derived field — collection rate % — used as a subtitle on
      // the row via a small helper below.
      _rate: g.rate,
    })),
    [collection],
  );

  const categoryChart = useMemo(() => {
    const rows = byCategory?.byCategory ?? [];
    return rows.slice(0, 10).map(r => ({
      name: r.name,
      billed: r.billed,
      collected: r.collected,
      outstanding: r.outstanding,
    }));
  }, [byCategory]);

  const forecastData = useMemo(
    () => (forecast?.upcoming ?? []).map(d => ({
      date: d.date,
      display: format(parseISO(d.date), 'MMM d'),
      amount: d.amount,
      invoiceCount: d.invoiceCount,
    })),
    [forecast],
  );

  /**
   * Overdue = every aging bucket except "current". Split out of Outstanding
   * so admins can see at a glance what's actively past-due (chase-worthy) vs
   * merely not-yet-due (informational).
   */
  const overdue = useMemo(() => {
    const buckets = aging?.buckets ?? [];
    let amount = 0;
    let count = 0;
    for (const b of buckets) {
      if (b.key === 'current') continue;
      amount += b.outstanding;
      count += b.count;
    }
    return { amount, count };
  }, [aging]);

  const trendData = useMemo(() => (trend?.trend ?? []).map(t => ({
    month: t.month,
    label: t.label,
    short: t.label.slice(0, 3),   // "Aug" from "Aug 2026"
    amount: t.amount,
    count: t.count,
  })), [trend]);

  const methodRows = useMemo(() => {
    const rows = (methodMix?.byMethod ?? []).map(r => ({
      key: r.method,
      label: PAYMENT_METHOD_LABEL[r.method],
      amount: r.amount,
      count: r.count,
    }));
    // Sort by amount desc so the largest slice reads first in the legend.
    return rows.sort((a, b) => b.amount - a.amount);
  }, [methodMix]);

  const methodTotalCollected = methodMix?.total.amount ?? 0;

  const topStudentRows = useMemo(() => (topStudents?.students ?? []), [topStudents]);

  // ─── Guards ─────────────────────────────────────────────────────────────

  if (!hasFeature('fees')) {
    return (
      <GuardCard
        icon={<BlockIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />}
        title="Fees module not included"
        message="Add the Fees plan feature to access reports."
      />
    );
  }
  if (!canRead('fee_reports')) {
    return (
      <GuardCard
        icon={<BlockIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />}
        title="Access Denied"
        message="You don't have permission to view Fee Reports."
      />
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} color="text.primary">Fee Reports</Typography>
        <Typography variant="body2" color="text.secondary">
          Aging, collection rate, category rollups, and dues forecast for the chosen session.
        </Typography>
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <SearchableSelect
            label="Session"
            value={sessionId}
            onChange={setSessionId}
            options={buildSessionOptions(sessions, theme)}
            clearable={false}
            minWidth={220}
          />
          <SearchableSelect
            label="Class"
            value={classId}
            onChange={setClassId}
            options={classes.map(c => ({ value: c._id, label: c.name }))}
            placeholder="All classes"
            minWidth={220}
          />
          <Box sx={{ flex: 1 }} />
          <SearchableSelect
            label="Forecast horizon"
            value={String(horizon)}
            onChange={v => setHorizon((Number(v) || 30) as Horizon)}
            options={HORIZON_OPTIONS.map(o => ({ value: String(o.value), label: o.label }))}
            clearable={false}
            minWidth={200}
          />
        </Stack>
      </Paper>

      {/* Concessions / Waivers / Late fees strip */}
      {adjustments && (
        <Paper
          elevation={0}
          sx={{
            p: 2, mb: 3, borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.text.primary, 0.015),
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0} alignItems={{ xs: 'stretch', sm: 'center' }} divider={<Divider orientation="vertical" flexItem />}>
            <StripCell
              label="Gross billed"
              value={currencyFmt(adjustments.totals.grossBilled)}
              tint="neutral"
              icon={<ReceiptLong fontSize="small" />}
              theme={theme}
            />
            <StripCell
              label="Concessions given"
              value={`− ${currencyFmt(adjustments.concessions.amount)}`}
              tint="info"
              icon={<TrendingDown fontSize="small" />}
              theme={theme}
            />
            <StripCell
              label="Waivers granted"
              value={`− ${currencyFmt(adjustments.waivers.amount)}`}
              sub={adjustments.waivers.count > 0 ? `${adjustments.waivers.count} waiver${adjustments.waivers.count === 1 ? '' : 's'}` : undefined}
              tint="warning"
              icon={<TrendingDown fontSize="small" />}
              theme={theme}
            />
            <StripCell
              label="Late fees added"
              value={`+ ${currencyFmt(adjustments.lateFees.amount)}`}
              sub={adjustments.lateFees.count > 0 ? `${adjustments.lateFees.count} challan${adjustments.lateFees.count === 1 ? '' : 's'}` : undefined}
              tint="error"
              icon={<TrendingUp fontSize="small" />}
              theme={theme}
            />
            <StripCell
              label="Net billed"
              value={currencyFmt(adjustments.totals.netBilled)}
              tint="success"
              icon={<ReceiptLong fontSize="small" />}
              theme={theme}
            />
          </Stack>
        </Paper>
      )}

      {/* KPI strip — 5 tiles: Billed, Collected, Outstanding, Overdue, Rate */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <KpiTile
            label="Billed"
            value={loading ? null : currencyFmt(kpi?.billed ?? 0)}
            hint={`${kpi?.invoiceCount ?? 0} challan${(kpi?.invoiceCount ?? 0) === 1 ? '' : 's'}`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <KpiTile
            label="Collected"
            value={loading ? null : currencyFmt(kpi?.collected ?? 0)}
            // Paid against challans — includes advances applied, so it can differ from cash received by method below.
            hint={kpi && kpi.billed > 0 ? `${Math.round((kpi.collected / kpi.billed) * 100)}% of billed · paid against challans` : undefined}
            accent="success"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <KpiTile
            label="Outstanding"
            value={loading ? null : currencyFmt(kpi?.outstanding ?? 0)}
            hint={aging ? `${aging.total.count} unpaid challan${aging.total.count === 1 ? '' : 's'}` : undefined}
            accent="warning"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <KpiTile
            label="Overdue"
            value={loading ? null : currencyFmt(overdue.amount)}
            hint={overdue.count > 0 ? `${overdue.count} past due · needs chasing` : 'Nothing past due'}
            accent="error"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <KpiTile
            label="Collection rate"
            value={loading ? null : `${Math.round((kpi?.rate ?? 0) * 100)}%`}
            hint="Collected / Billed"
            accent="info"
          />
        </Grid>
      </Grid>

      {/* Aging chart — full width */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <ChartCard
            title="Aging"
            subtitle={aging ? `Outstanding as of ${format(new Date(aging.asOfDate), 'MMM d, yyyy')}` : 'Outstanding by days-since-due'}
            loading={loading}
            empty={!loading && agingChartData.every(b => b.outstanding === 0)}
            emptyMessage="Nothing outstanding — every challan in this scope is settled."
            height={280}
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={agingChartData} margin={{ top: 12, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 4" stroke={theme.palette.divider} strokeOpacity={0.4} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: theme.palette.text.primary }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => formatMoney(v, currency)}
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 1200, pointerEvents: 'none' }}
                  contentStyle={{
                    background: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
                  formatter={(value: number, _name: string, ctx: any) => [
                    formatMoneyFull(value, currency),
                    `Outstanding · ${ctx?.payload?.count ?? 0} challan${ctx?.payload?.count === 1 ? '' : 's'}`,
                  ]}
                />
                <Bar dataKey="outstanding" radius={[6, 6, 0, 0]} maxBarSize={64}>
                  {agingChartData.map((row, i) => (
                    <Cell
                      key={i}
                      fill={
                        row.label === 'Not yet due' ? theme.palette.info.main
                        : row.label === '1–30 days' ? theme.palette.warning.light
                        : row.label === '31–60 days' ? theme.palette.warning.main
                        : row.label === '61–90 days' ? theme.palette.warning.dark
                        : theme.palette.error.main
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Collection trend — 12-month area chart, full width */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <ChartCard
            title="Collection trend"
            subtitle={trend
              ? `${currencyFmt(trend.total.amount)} collected across ${trend.total.count} payment${trend.total.count === 1 ? '' : 's'} · last ${trend.window.months} months`
              : 'Last 12 months of received payments'}
            right={trend ? <MoMChip change={trend.momChange} /> : undefined}
            loading={loading}
            empty={!loading && trendData.every(d => d.amount === 0)}
            emptyMessage="No payments received in this window yet."
            height={260}
          >
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trendData} margin={{ top: 12, right: 16, left: 8, bottom: 8 }}>
                <defs>
                  <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={theme.palette.success.main} stopOpacity={theme.palette.mode === 'dark' ? 0.45 : 0.28} />
                    <stop offset="100%" stopColor={theme.palette.success.main} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 4" stroke={theme.palette.divider} strokeOpacity={0.4} vertical={false} />
                <XAxis
                  dataKey="short"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => formatMoney(v, currency)}
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 1200, pointerEvents: 'none' }}
                  contentStyle={{
                    background: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  cursor={{ stroke: alpha(theme.palette.text.primary, 0.2), strokeWidth: 1 }}
                  formatter={(value: number, _name: string, ctx: any) => [
                    formatMoneyFull(value, currency),
                    `${ctx?.payload?.count ?? 0} payment${ctx?.payload?.count === 1 ? '' : 's'}`,
                  ]}
                  labelFormatter={(_l: string, payload: any) => payload?.[0]?.payload?.label ?? ''}
                />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke={theme.palette.success.main}
                  strokeWidth={2.5}
                  fill="url(#trendFill)"
                  activeDot={{
                    r: 5,
                    stroke: theme.palette.background.paper,
                    strokeWidth: 2,
                    fill: theme.palette.success.main,
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Top outstanding students + Payment method mix */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <ChartCard
            title="Top outstanding students"
            subtitle={topStudentRows.length > 0
              ? `Top ${topStudentRows.length} · highest dues first · click a row to open their challans`
              : 'Highest dues first — click a row to open the student\'s challans'}
            right={
              <SearchableSelect
                label="Class"
                value={topStudentsClassId}
                onChange={setTopStudentsClassId}
                options={classes.map(c => ({ value: c._id, label: c.name }))}
                placeholder="All classes"
                minWidth={180}
              />
            }
            loading={topStudentsLoading}
            empty={!topStudentsLoading && topStudentRows.length === 0}
            emptyMessage="No students with outstanding dues in this scope."
            height={340}
          >
            <Box
              sx={{
                maxHeight: 340,
                overflowY: 'auto',
                pr: 0.5,
                // Themed scrollbar so the list feels intentional in both
                // light and dark modes rather than defaulting to the OS
                // scrollbar which visually clashes with the card chrome.
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  background: alpha(theme.palette.text.primary, 0.15),
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: alpha(theme.palette.text.primary, 0.28),
                },
                scrollbarWidth: 'thin',
                scrollbarColor: `${alpha(theme.palette.text.primary, 0.2)} transparent`,
              }}
            >
            <Stack divider={<Divider sx={{ borderColor: alpha(theme.palette.divider, 0.6) }} />}>
              {topStudentRows.map((r, idx) => {
                const isOverdue = r.oldestOverdueDays > 0;
                const overdueColor = r.oldestOverdueDays > 60
                  ? theme.palette.error.main
                  : r.oldestOverdueDays > 30
                    ? theme.palette.warning.main
                    : theme.palette.info.main;
                return (
                  <Box
                    key={r.studentId}
                    onClick={() => navigate(`/app/fees/invoices?studentId=${r.studentId}`)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 1, py: 1.1,
                      cursor: 'pointer',
                      borderRadius: 1.25,
                      transition: 'background 120ms',
                      '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.03) },
                    }}
                  >
                    <Box sx={{
                      width: 26, height: 26, borderRadius: 1.25,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700,
                      color: theme.palette.text.secondary,
                      bgcolor: alpha(theme.palette.text.primary, 0.04),
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
                    }}>
                      {idx + 1}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={0.75} alignItems="baseline">
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {r.studentName}
                        </Typography>
                        {r.grNumber && (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled' }} noWrap>
                            {r.grNumber}
                          </Typography>
                        )}
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {r.className ?? '—'}
                          {r.sectionName ? ` · ${r.sectionName}` : ''}
                          {r.rollNumber != null ? ` · Roll ${r.rollNumber}` : ''}
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack alignItems="flex-end" spacing={0.25} sx={{ flexShrink: 0 }}>
                      <Typography variant="body2" fontWeight={700} sx={{
                        fontVariantNumeric: 'tabular-nums',
                        color: theme.palette.text.primary,
                      }}>
                        {currencyFmt(r.outstanding)}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {isOverdue && (
                          <Chip
                            icon={<WarningAmberOutlined sx={{ fontSize: '0.85rem !important' }} />}
                            label={`${r.oldestOverdueDays}d overdue`}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20, fontSize: '0.68rem', fontWeight: 600,
                              borderColor: alpha(overdueColor, 0.4),
                              color: overdueColor,
                              bgcolor: alpha(overdueColor, 0.06),
                              '& .MuiChip-icon': { color: 'inherit' },
                            }}
                          />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {r.invoiceCount} inv
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
            </Box>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <ChartCard
            title="Payment methods"
            subtitle={methodMix ? `${currencyFmt(methodTotalCollected)} received · ${methodMix.total.count} payment${methodMix.total.count === 1 ? '' : 's'}` : 'Split of collected amounts by method'}
            loading={loading}
            empty={!loading && methodTotalCollected === 0}
            emptyMessage="No payments received yet."
            height={320}
          >
            <Stack spacing={1.5} sx={{ height: '100%' }}>
              <Box sx={{ position: 'relative', width: '100%', height: 200 }}>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={methodRows}
                      dataKey="amount"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius={62}
                      outerRadius={92}
                      paddingAngle={methodRows.filter(r => r.amount > 0).length > 1 ? 2 : 0}
                      stroke={theme.palette.background.paper}
                      strokeWidth={3}
                    >
                      {methodRows.map((row) => (
                        <Cell key={row.key} fill={methodPalette(theme)[row.key]} />
                      ))}
                    </Pie>
                    <Tooltip
                      wrapperStyle={{ zIndex: 1200, pointerEvents: 'none' }}
                      contentStyle={{
                        background: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(value: number, name: string, ctx: any) => [
                        `${formatMoneyFull(value, currency)} · ${ctx?.payload?.count ?? 0} payment${ctx?.payload?.count === 1 ? '' : 's'}`,
                        name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{
                  position: 'absolute', inset: 0,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  pointerEvents: 'none',
                }}>
                  <Typography variant="caption" color="text.secondary">Total</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{
                    fontVariantNumeric: 'tabular-nums',
                    color: theme.palette.text.primary,
                    lineHeight: 1.1,
                  }}>
                    {formatMoney(methodTotalCollected, currency)}
                  </Typography>
                </Box>
              </Box>

              <Stack spacing={0.5}>
                {methodRows.map((r) => {
                  const pct = methodTotalCollected > 0 ? Math.round((r.amount / methodTotalCollected) * 100) : 0;
                  const color = methodPalette(theme)[r.key];
                  return (
                    <Stack key={r.key} direction="row" alignItems="center" spacing={1.25} sx={{ py: 0.25 }}>
                      <Box sx={{
                        width: 26, height: 26, borderRadius: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color, bgcolor: alpha(color, 0.1),
                      }}>
                        {methodIcon[r.key]}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Typography variant="caption" fontWeight={600} sx={{ color: theme.palette.text.primary }}>
                            {r.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            · {pct}%
                          </Typography>
                        </Stack>
                      </Box>
                      <Typography variant="caption" fontWeight={700} sx={{
                        fontVariantNumeric: 'tabular-nums',
                        color: theme.palette.text.primary,
                      }}>
                        {currencyFmt(r.amount)}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          </ChartCard>
        </Grid>
      </Grid>

      {/* Composition — By Category | By Class */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="By fee category"
            subtitle="Billed vs collected per fee head"
            loading={loading}
            empty={!loading && categoryChart.length === 0}
            emptyMessage="No challans in this window."
            height={Math.max(240, categoryChart.length * 44 + 40)}
          >
            <ResponsiveContainer width="100%" height={Math.max(240, categoryChart.length * 44 + 40)}>
              <BarChart
                layout="vertical"
                data={categoryChart}
                margin={{ top: 8, right: 24, left: 8, bottom: 0 }}
                stackOffset="sign"
              >
                <CartesianGrid strokeDasharray="3 4" stroke={theme.palette.divider} strokeOpacity={0.4} horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => formatMoney(v, currency)}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fill: theme.palette.text.primary }}
                  axisLine={false}
                  tickLine={false}
                  width={130}
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 1200, pointerEvents: 'none' }}
                  contentStyle={{
                    background: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
                  formatter={(value: number, name: string) => [
                    formatMoneyFull(value, currency),
                    name === 'collected' ? 'Collected' : 'Outstanding',
                  ]}
                />
                <Bar dataKey="collected"   stackId="s" fill={theme.palette.success.main} maxBarSize={22} radius={[0, 0, 0, 0]} />
                <Bar dataKey="outstanding" stackId="s" fill={theme.palette.warning.main} maxBarSize={22} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Aging by class"
            subtitle="Outstanding rolled up by class — top 10"
            loading={loading}
            empty={!loading && agingByClassRows.length === 0}
            emptyMessage="Nothing outstanding across classes."
            height={Math.max(240, agingByClassRows.length * 36 + 40)}
          >
            <HorizontalBarList rows={agingByClassRows} useCategoricalColors />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Collection rate by class + Forecast */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Collection rate by class"
            subtitle="Billed vs collected, top 10 by billed"
            loading={loading}
            empty={!loading && collectionByClassRows.length === 0}
            emptyMessage="No billing in this window."
            height={Math.max(240, collectionByClassRows.length * 36 + 40)}
          >
            <Stack spacing={0.75}>
              {collectionByClassRows.map((r, i) => (
                <Box key={i}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                    <Typography variant="caption" sx={{ minWidth: 100, color: theme.palette.text.primary, fontWeight: 500 }} noWrap>
                      {r.label}
                    </Typography>
                    <Box sx={{
                      flex: 1, position: 'relative', height: 12, borderRadius: 6,
                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                      overflow: 'hidden',
                    }}>
                      <Box sx={{
                        position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: `${Math.min(100, Math.round((r._rate ?? 0) * 100))}%`,
                        bgcolor: theme.palette.success.main,
                        borderRadius: 6,
                        transition: 'width 300ms',
                      }} />
                    </Box>
                    <Typography variant="caption" sx={{ minWidth: 44, textAlign: 'right', fontWeight: 700, color: (r._rate ?? 0) >= 0.9 ? theme.palette.success.dark : theme.palette.text.primary }}>
                      {Math.round((r._rate ?? 0) * 100)}%
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 12.5 /* aligns after label width */, display: 'block' }}>
                    {currencyFmt(r.amount)} billed · {r.count} challan{r.count === 1 ? '' : 's'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </ChartCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Dues forecast"
            subtitle={forecast ? `Upcoming ${forecast.window.horizonDays} days · ${currencyFmt(forecast.total.amount)} across ${forecast.total.invoiceCount} challan${forecast.total.invoiceCount === 1 ? '' : 's'}` : `Upcoming ${horizon} days`}
            loading={loading}
            empty={!loading && forecastData.every(d => d.amount === 0)}
            emptyMessage="No challans due in this window."
            height={280}
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={forecastData} margin={{ top: 12, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 4" stroke={theme.palette.divider} strokeOpacity={0.4} vertical={false} />
                <XAxis
                  dataKey="display"
                  tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                  interval={Math.max(0, Math.floor(forecastData.length / 10) - 1)}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => formatMoney(v, currency)}
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 1200, pointerEvents: 'none' }}
                  contentStyle={{
                    background: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
                  formatter={(value: number, _name: string, ctx: any) => [
                    formatMoneyFull(value, currency),
                    `${ctx?.payload?.invoiceCount ?? 0} challan${ctx?.payload?.invoiceCount === 1 ? '' : 's'}`,
                  ]}
                  labelFormatter={(l: string, payload: any) =>
                    payload?.[0]?.payload?.date
                      ? format(parseISO(payload[0].payload.date), 'EEE, MMM d, yyyy')
                      : l
                  }
                />
                <Bar dataKey="amount" fill={theme.palette.info.main} radius={[3, 3, 0, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

// ─── Local subcomponents ─────────────────────────────────────────────────────

interface KpiTileProps {
  label: string;
  value: string | null;
  hint?: string;
  accent?: 'success' | 'warning' | 'info' | 'error';
}

const KpiTile: React.FC<KpiTileProps> = ({ label, value, hint, accent }) => {
  const theme = useTheme();
  const color = accent === 'success' ? theme.palette.success
              : accent === 'warning' ? theme.palette.warning
              : accent === 'info'    ? theme.palette.info
              : accent === 'error'   ? theme.palette.error
              : null;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${color ? alpha(color.main, 0.25) : theme.palette.divider}`,
        bgcolor: color ? alpha(color.main, 0.04) : 'transparent',
        height: '100%',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      {value === null ? (
        <Skeleton variant="text" width="60%" height={32} />
      ) : (
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{
            color: color ? color.dark : theme.palette.text.primary,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>
      )}
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Paper>
  );
};

/**
 * One cell in the Concessions / Waivers / Late-fees ribbon. Icon-left,
 * label + value stacked, optional sub-line for counts. Uses semantic MUI
 * palette tones so the ribbon reads at a glance without external legend.
 */
const StripCell: React.FC<{
  label: string;
  value: string;
  sub?: string;
  tint: 'neutral' | 'info' | 'warning' | 'error' | 'success';
  icon: React.ReactNode;
  theme: any;
}> = ({ label, value, sub, tint, icon, theme }) => {
  const palette = tint === 'neutral' ? theme.palette.text : theme.palette[tint];
  const color = (palette as any).dark ?? (palette as any).main ?? theme.palette.text.primary;
  const bg    = tint === 'neutral' ? alpha(theme.palette.text.primary, 0.05) : alpha((palette as any).main, 0.1);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1, minWidth: 0, py: { xs: 1, sm: 0.5 }, px: { xs: 0, sm: 1.5 } }}>
      <Box sx={{
        width: 34, height: 34, borderRadius: 1.25,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: bg, color, flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }} noWrap>
          {label}
        </Typography>
        <Typography variant="subtitle1" fontWeight={800} sx={{
          color,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.2,
        }} noWrap>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }} noWrap>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/**
 * Small chip that shows the month-over-month delta for the trend line.
 * Green up-arrow for growth, red down for decline, grey flat when neutral.
 */
const MoMChip: React.FC<{ change: number }> = ({ change }) => {
  const theme = useTheme();
  const pct = Math.round(change * 100);
  const direction = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  const color = direction === 'up' ? theme.palette.success
             : direction === 'down' ? theme.palette.error
             : theme.palette.text;
  const Icon = direction === 'up' ? TrendingUp
             : direction === 'down' ? TrendingDown
             : TrendingFlat;
  const label = direction === 'flat'
    ? 'No change vs prev. month'
    : `${pct > 0 ? '+' : ''}${pct}% vs prev. month`;
  return (
    <Chip
      icon={<Icon sx={{ fontSize: '1rem !important' }} />}
      label={label}
      size="small"
      variant="outlined"
      sx={{
        height: 26, fontWeight: 700, fontSize: '0.72rem',
        borderColor: direction === 'flat'
          ? alpha(theme.palette.text.primary, 0.15)
          : alpha((color as any).main, 0.35),
        color: direction === 'flat'
          ? theme.palette.text.secondary
          : (color as any).dark,
        bgcolor: direction === 'flat'
          ? alpha(theme.palette.text.primary, 0.03)
          : alpha((color as any).main, 0.08),
        '& .MuiChip-icon': { color: 'inherit' },
      }}
    />
  );
};

const GuardCard: React.FC<{ icon: React.ReactNode; title: string; message: string }> = ({ icon, title, message }) => {
  const theme = useTheme();
  return (
    <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <Paper elevation={0} sx={{ p: 4, textAlign: 'center', maxWidth: 400, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
        {icon}
        <Typography variant="h6" fontWeight={600} gutterBottom>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{message}</Typography>
      </Paper>
    </Box>
  );
};

export default FeeReportsPage;
